import type { AnalyticsSalesByCountry } from "@/@types/Analytics";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { countries } from "country-data-list";
import { Globe2, List } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
import { GEO_URL, PRIMARY_COLOR, PRIMARY_COLOR_HOVER } from "../constants";
import { ChartEmpty } from "./ChartParts";

const alpha3ToAlpha2: Record<string, string> = {};
const mapNameToAlpha2: Record<string, string> = {};
countries.all.forEach((c: any) => {
  if (c.alpha3 && c.alpha2) alpha3ToAlpha2[c.alpha3] = c.alpha2;
  if (c.alpha2 && c.name) mapNameToAlpha2[c.name] = c.alpha2;
});
mapNameToAlpha2["United States of America"] = "US";

const toAlpha2 = (code: string) => alpha3ToAlpha2[code] || code;

function CountryTooltip({ sales }: { sales: AnalyticsSalesByCountry }) {
  const { t } = useTranslation("reports");

  return (
    <div className="bg-zinc-900/90 backdrop-blur-sm p-3 rounded-md min-w-[180px] border border-zinc-800">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{sales.flag_emoji}</span>
        <span className="text-white font-medium text-sm">{sales.country_name}</span>
      </div>
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-400">{t("reports:value_label")}</span>
          <span className="text-white font-medium">{sales.amount_formatted}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-400">{sales.count} {t("reports:sales_unit")}</span>
          <span className="text-zinc-400">{sales.percentage.toFixed(1)}%</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-zinc-400">AOV</span>
          <span className="text-primary font-medium">{sales.average_order_value_formatted}</span>
        </div>
      </div>
    </div>
  );
}

interface SalesByCountryCardProps {
  salesByCountry?: AnalyticsSalesByCountry[];
  isLoading: boolean;
}

export function SalesByCountryCard({ salesByCountry, isLoading }: SalesByCountryCardProps) {
  const { t } = useTranslation("reports");
  const [activeCountryCode, setActiveCountryCode] = useState<string | null>(null);
  const rows = useMemo(() => salesByCountry ?? [], [salesByCountry]);

  const salesByAlpha2 = useMemo(
    () => new Map(rows.map((row) => [toAlpha2(row.country_code), row])),
    [rows],
  );

  const maxCount = useMemo(() => Math.max(...rows.map((c) => c.count), 1), [rows]);
  const activeSales = activeCountryCode ? salesByAlpha2.get(activeCountryCode) : undefined;

  const renderBody = (content: ReactNode) => {
    if (isLoading) {
      return (
        <div className="p-4">
          <Skeleton className="w-full h-[280px]" />
        </div>
      );
    }
    if (rows.length === 0) return <ChartEmpty className="h-[280px]" />;
    return content;
  };

  return (
    <Card className="overflow-hidden h-full">
      <Tabs defaultValue="map" className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-colorBorder">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-colorText truncate">
              {t("reports:chart_sales_by_country")}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {t("reports:chart_sales_by_country_desc")}
            </p>
          </div>

          <TabsList className="grid grid-cols-2 h-9 bg-zinc-900 rounded-lg gap-2 ml-4 flex-shrink-0">
            <TabsTrigger
              value="map"
              className="group h-7 w-7 p-0 rounded-md"
              aria-label={t("reports:map_view")}
            >
              <Globe2 className="w-4 h-4 text-zinc-400 group-data-[state=active]:text-white" />
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="group h-7 w-7 p-0 rounded-md"
              aria-label={t("reports:list_view")}
            >
              <List className="w-4 h-4 text-zinc-400 group-data-[state=active]:text-white" />
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="map" className="mt-0 flex-1 min-h-0">
          {renderBody(
            <div className="relative h-full min-h-[280px] w-full">
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 130 }}
                className="w-full h-full"
              >
                <ZoomableGroup center={[0, 20]} zoom={1.2}>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo, index) => {
                        const code = mapNameToAlpha2[geo.properties.name] ?? null;
                        return (
                          <Geography
                            key={geo.rsmKey + index}
                            geography={geo}
                            fill={code && salesByAlpha2.has(code) ? PRIMARY_COLOR : "#27272A"}
                            stroke="#374151"
                            strokeWidth={0.5}
                            onMouseEnter={() => setActiveCountryCode(code)}
                            onMouseLeave={() => setActiveCountryCode(null)}
                            style={{
                              default: { outline: "none" },
                              hover: { outline: "none", cursor: "pointer", fill: PRIMARY_COLOR_HOVER },
                              pressed: { outline: "none" },
                            }}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>

              {activeSales && (
                <div className="absolute top-4 left-4 z-10">
                  <CountryTooltip sales={activeSales} />
                </div>
              )}
            </div>,
          )}
        </TabsContent>

        <TabsContent value="list" className="mt-0 flex-1 min-h-0 overflow-auto">
          {renderBody(
            <div className="p-4 space-y-3 min-h-[280px] max-h-[400px] overflow-y-auto">
              {rows.map((country, index) => (
                <div key={country.country_code + index} className="relative group">
                  <div
                    className="absolute left-0 top-0 h-full bg-zinc-900 rounded-lg transition-all duration-300"
                    style={{ width: `${(country.count / maxCount) * 100}%` }}
                  />
                  <div className="relative flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 z-10">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{country.flag_emoji}</span>
                      <div className="min-w-0">
                        <span className="text-white text-sm font-medium block">
                          {country.country_name}
                        </span>
                        <p className="text-xs text-zinc-400">
                          AOV: {country.average_order_value_formatted}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">
                        {country.count} vendas
                      </div>
                      <div className="text-zinc-400 text-xs">
                        {country.amount_formatted}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>,
          )}
        </TabsContent>
      </Tabs>
    </Card>
  );
}
